import React, { useEffect, useMemo, useState } from "react";
import { useMsal } from "@azure/msal-react";
import DataTable, { TableColumn } from "react-data-table-component";
import { FileWarning, AlertTriangle, Download, ExternalLink, RefreshCw } from "lucide-react";
import { getApiBaseUrl } from "../utils/baseUrl";
import { useLoading, acquireTokenSafe } from "../App";
import DataSourcePanel from "./DataSourcePanel";

interface Discrepancy {
    ClientID: string;
    Title: string;
    CompanyNumber: string;
    CroCompanyNumber?: string;
    Field?: string;
    SentaValue?: string;
    CroValue?: string;
    Error?: string;
}

interface Report {
    name: string;
    timestamp: string;
    discrepancies: Discrepancy[];
}

interface ApiResponse {
    reports: Report[];
    configured: boolean;
    message?: string;
}

const tableStyles = {
    table: { style: { backgroundColor: "var(--bg-secondary)", borderRadius: "8px" } },
    headRow: {
        style: {
            backgroundColor: "var(--bg-tertiary)",
            borderBottomColor: "var(--border-color)",
            color: "var(--text-primary)",
            fontWeight: 600,
            fontSize: "0.85rem",
        },
    },
    headCells: { style: { color: "var(--text-secondary)", paddingLeft: "16px", paddingRight: "16px" } },
    rows: {
        style: {
            backgroundColor: "var(--bg-secondary)",
            color: "var(--text-primary)",
            borderBottomColor: "var(--border-color)",
            minHeight: "48px",
        },
        highlightOnHoverStyle: {
            backgroundColor: "var(--bg-hover)",
            color: "var(--text-primary)",
            borderBottomColor: "var(--border-color)",
        },
    },
    cells: { style: { paddingLeft: "16px", paddingRight: "16px" } },
    pagination: {
        style: {
            backgroundColor: "var(--bg-secondary)",
            color: "var(--text-secondary)",
            borderTopColor: "var(--border-color)",
        },
        pageButtonsStyle: {
            color: "var(--text-secondary)",
            fill: "var(--text-secondary)",
            "&:hover:not(:disabled)": { backgroundColor: "var(--bg-hover)" },
            "&:disabled": { opacity: 0.4 },
        },
    },
    noData: { style: { backgroundColor: "var(--bg-secondary)", color: "var(--text-secondary)" } },
};

const today = () => new Date().toISOString().split("T")[0];

const CroDiscrepancies: React.FC = () => {
    const { instance, accounts } = useMsal();
    const { setLoading } = useLoading();
    const [reports, setReports] = useState<Report[]>([]);
    const [selected, setSelected] = useState<string>("");
    const [configured, setConfigured] = useState<boolean>(true);
    const [serverMessage, setServerMessage] = useState<string | null>(null);
    const [search, setSearch] = useState("");
    const [fieldFilter, setFieldFilter] = useState<string>("all");
    const [error, setError] = useState<string | null>(null);

    const fetchData = async () => {
        if (accounts.length === 0) return;
        setLoading(true);
        try {
            const token = await acquireTokenSafe(instance, accounts);
            if (!token) { setLoading(false); return; }
            const res = await fetch(`${getApiBaseUrl()}/api/cro-discrepancies`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data: ApiResponse = await res.json();
            const sorted = (data.reports || []).slice().sort(
                (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
            );
            setReports(sorted);
            setSelected(sorted[0]?.name || "");
            setConfigured(!!data.configured);
            setServerMessage(data.message || null);
            setError(null);
        } catch (err: any) {
            setError(err.message || "Failed to load discrepancies");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, [instance, accounts]);

    const currentReport = useMemo(
        () => reports.find(r => r.name === selected) || null,
        [reports, selected]
    );

    const fieldOptions = useMemo(() => {
        if (!currentReport) return [];
        const set = new Set<string>();
        currentReport.discrepancies.forEach(d => {
            if (d.Field) set.add(d.Field);
            if (d.Error) set.add(d.Error);
        });
        return Array.from(set).sort();
    }, [currentReport]);

    const filtered = useMemo(() => {
        if (!currentReport) return [];
        let data = currentReport.discrepancies;
        if (search.trim()) {
            const s = search.toLowerCase();
            data = data.filter(row =>
                Object.values(row).some(v => v && v.toString().toLowerCase().includes(s))
            );
        }
        if (fieldFilter !== "all") {
            data = data.filter(row => row.Field === fieldFilter || row.Error === fieldFilter);
        }
        return data;
    }, [currentReport, search, fieldFilter]);

    const columns: TableColumn<Discrepancy>[] = [
        {
            name: "Client ID",
            selector: row => row.ClientID,
            sortable: true,
            cell: row => (
                <a
                    href={`https://kinore.senta.co/c/v/${row.ClientID}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "var(--accent-blue)", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}
                >
                    {row.ClientID}
                    <ExternalLink size={12} />
                </a>
            ),
            width: "150px",
        },
        { name: "Company", selector: row => row.Title, sortable: true },
        { name: "Senta CRN", selector: row => row.CompanyNumber || "—", sortable: true, width: "120px" },
        { name: "CRO CRN", selector: row => row.CroCompanyNumber || "—", sortable: true, width: "120px" },
        { name: "Field", selector: row => row.Field || "—", sortable: true, width: "140px" },
        { name: "Senta Value", selector: row => row.SentaValue || "—", sortable: true },
        { name: "CRO Value", selector: row => row.CroValue || "—", sortable: true },
        { name: "Error", selector: row => row.Error || "—", sortable: true },
    ];

    const downloadCSV = () => {
        const headers = ["Client ID", "Company", "Senta CRN", "CRO CRN", "Field", "Senta Value", "CRO Value", "Error"];
        const rows = filtered.map(d => [
            d.ClientID, d.Title, d.CompanyNumber || "", d.CroCompanyNumber || "",
            d.Field || "", d.SentaValue || "", d.CroValue || "", d.Error || "",
        ]);
        const esc = (v: any) => {
            const s = String(v ?? "");
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const csv = [headers.join(","), ...rows.map(r => r.map(esc).join(","))].join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `cro-discrepancies-${today()}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="page-container">
            <div className="page-header compact">
                <div className="page-title">
                    <FileWarning size={20} />
                    <h1>CRO Discrepancies</h1>
                </div>
            </div>

            {!configured && (
                <div className="warning-banner">
                    <AlertTriangle size={20} />
                    <span>{serverMessage || "CRO API credentials are not configured on the server. The report is showing no data."}</span>
                </div>
            )}

            {error && (
                <div className="warning-banner">
                    <AlertTriangle size={20} />
                    <span>Error loading data: {error}</span>
                </div>
            )}

            <div className="stats-grid stats-grid-3">
                <div className="stat-card">
                    <div className="stat-icon"><FileWarning size={20} /></div>
                    <div className="stat-content">
                        <div className="stat-value">{reports.length}</div>
                        <div className="stat-label">Snapshots</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ color: "#dc2626" }}><AlertTriangle size={20} /></div>
                    <div className="stat-content">
                        <div className="stat-value">{filtered.length}</div>
                        <div className="stat-label">Discrepancies</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon"><FileWarning size={20} /></div>
                    <div className="stat-content">
                        <div className="stat-value" style={{ fontSize: "0.95rem" }}>
                            {currentReport ? new Date(currentReport.timestamp).toLocaleDateString("en-IE") : "—"}
                        </div>
                        <div className="stat-label">Snapshot Date</div>
                    </div>
                </div>
            </div>

            <div className="table-controls">
                <input
                    type="text"
                    className="search-input"
                    placeholder="Search…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
                <select
                    className="filter-select"
                    value={selected}
                    onChange={e => setSelected(e.target.value)}
                    disabled={reports.length === 0}
                >
                    {reports.length === 0 && <option value="">No snapshots</option>}
                    {reports.map(r => (
                        <option key={r.name} value={r.name}>
                            {new Date(r.timestamp).toLocaleString("en-IE")}
                        </option>
                    ))}
                </select>
                <select
                    className="filter-select"
                    value={fieldFilter}
                    onChange={e => setFieldFilter(e.target.value)}
                >
                    <option value="all">All Fields</option>
                    {fieldOptions.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
                <button onClick={fetchData} className="download-btn" title="Refresh">
                    <RefreshCw size={16} /> Refresh
                </button>
                <button onClick={downloadCSV} className="download-btn" title="Download CSV">
                    <Download size={16} /> Export CSV
                </button>
            </div>

            <div className="data-table-container">
                <DataTable
                    columns={columns}
                    data={filtered}
                    pagination
                    paginationPerPage={15}
                    paginationRowsPerPageOptions={[15, 25, 50, 100]}
                    highlightOnHover
                    responsive
                    noDataComponent={<div className="no-data">No discrepancies in this snapshot.</div>}
                    customStyles={tableStyles}
                />
            </div>

            <DataSourcePanel
                summary={
                    <>
                        Field-by-field comparison of Senta company records against the CRO (Companies
                        Registration Office) authoritative dataset. Each snapshot is a frozen point-in-time
                        comparison.
                    </>
                }
                badge={configured ? "PRODUCTION DATA" : "NOT CONFIGURED"}
                badgeColor={configured ? undefined : "#f59e0b"}
                sections={[
                    {
                        title: "Backend endpoint",
                        items: [
                            { name: "GET /api/cro-discrepancies", description: "Returns the list of CRO comparison snapshots. Empty until credentials are configured." },
                        ],
                    },
                    {
                        title: "Required environment variables",
                        items: [
                            { name: "CRO_USERNAME", description: "CRO Open Services account username/email." },
                            { name: "CRO_API_KEY", description: "CRO Open Services API key (HTTP Basic auth)." },
                        ],
                    },
                    {
                        title: "Database tables read (after sync wired)",
                        items: [
                            { name: "senta_companies", description: "Local Senta snapshot supplying the Senta-side values for comparison." },
                            { name: "cro_snapshots (planned)", description: "Will store CRO API responses keyed by company number once the sync job is enabled." },
                        ],
                    },
                ]}
            />
        </div>
    );
};

export default CroDiscrepancies;
